'use client';

import Components from '@/components/component_registry';
import { useEffect, useMemo, useState } from 'react';
import { EditorDrawer } from './EditorDrawer';
import { useEditor } from './context/EditorContext/EditorContext';

const HIDDEN_COMPONENT_OPTIONS = new Set([
    'Nav',
    'Footer',
    'Popup',
    'ReviewSlider',
    'ShapeDivider',
]);
const PREVIEW_EXTENSIONS = ['webp', 'png', 'jpg', 'jpeg'];
const CATEGORY_DEFINITIONS = [
    {
        label: 'Heroes',
        components: [
            'Hero',
            'HeroFullBG',
            'HeroHalf',
            'AboutPageHero',
            'FaqHero',
            'ReviewPage',
            'WorkPage',
        ],
    },
    {
        label: 'USPs & Trust',
        components: [
            'USP',
            'Credentials',
            'CredentialsLine',
            'SatisfactionGuarantee',
            'TrustBadge',
            'Recommend',
        ],
    },
    {
        label: 'Reviews',
        components: ['Reviews', 'ReviewHero', 'ReviewFloat', 'ReviewStories'],
    },
    {
        label: 'Services',
        components: ['Services', 'Service', 'SecondaryServices', 'ServiceTabs'],
    },
    {
        label: 'Galleries',
        components: ['Gallery', 'ImageGallery', 'ImageSlider', 'WorkStories'],
    },
    {
        label: 'Grids & Pricing',
        components: [
            'ItemGrid',
            'ItemCounter',
            'List',
            'ComparisonTable',
            'Pricing',
            'Process',
        ],
    },
    {
        label: 'Pages & Sections',
        components: [
            'Faq',
            'AboutFeature',
            'Team',
            'Privacy',
            'TermsAndConditions',
            'Financial',
        ],
    },
    { label: 'CTA', components: ['CallToAction', 'CTAHero'] },
    {
        label: 'Utilities',
        components: ['GoogleMap', 'Popup', 'Basic', 'CustomCode'],
    },
];

function formatComponentName(name) {
    return String(name || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim();
}

export function AddComponent() {
    const editor = useEditor();
    const open = editor.components.add_panel.visible();
    const targetIndex = editor.components.add_panel.index();

    const [componentSearch, setComponentSearch] = useState('');
    const [previewIndexByComponent, setPreviewIndexByComponent] = useState({});

    useEffect(() => {
        if (!open) return;
        setComponentSearch('');
    }, [open]);

    const componentOptions = useMemo(
        () =>
            Object.keys(Components)
                .sort()
                .filter(
                    (componentName) =>
                        !HIDDEN_COMPONENT_OPTIONS.has(componentName)
                ),
        []
    );

    const filteredComponentOptions = useMemo(() => {
        const q = componentSearch.trim().toLowerCase();
        if (!q) return componentOptions;
        return componentOptions.filter((componentName) => {
            const display = formatComponentName(componentName).toLowerCase();
            return (
                componentName.toLowerCase().includes(q) || display.includes(q)
            );
        });
    }, [componentOptions, componentSearch]);

    const groupedComponentOptions = useMemo(() => {
        const available = new Set(filteredComponentOptions);
        const groups = [];

        for (const category of CATEGORY_DEFINITIONS) {
            const items = category.components.filter((name) =>
                available.has(name)
            );
            if (items.length === 0) continue;
            groups.push({ label: category.label, items });
            items.forEach((name) => available.delete(name));
        }

        if (available.size > 0) {
            groups.push({
                label: 'Other',
                items: [...available].sort((a, b) => a.localeCompare(b)),
            });
        }

        return groups;
    }, [filteredComponentOptions]);

    const getPreviewSrc = (componentName) => {
        const idx = previewIndexByComponent[componentName] ?? 0;
        if (idx < 0 || idx >= PREVIEW_EXTENSIONS.length) return null;
        return `/editor-assets/component-previews/${componentName}.${PREVIEW_EXTENSIONS[idx]}`;
    };

    const handlePreviewError = (componentName) => {
        setPreviewIndexByComponent((prev) => {
            const current = prev[componentName] ?? 0;
            if (current >= PREVIEW_EXTENSIONS.length - 1) {
                return { ...prev, [componentName]: -1 };
            }
            return { ...prev, [componentName]: current + 1 };
        });
    };

    const handleAddComponent = (componentName) => {
        if (targetIndex == null || targetIndex < 0) return;
        editor.components.add({
            index: targetIndex,
            component_name: componentName,
        });
    };

    return (
        <EditorDrawer
            storageKey="globalEditorDrawerv2"
            title="Add Component"
            open={open}
            onClose={editor.components.add_panel.close}
            hideFooter
        >
            <div className="space-y-4 py-4">
                <input
                    id="component-picker-search"
                    type="text"
                    value={componentSearch}
                    onChange={(e) => setComponentSearch(e.target.value)}
                    placeholder="Search a component"
                    className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-800 outline-none transition focus:border-blue-400"
                />

                {groupedComponentOptions.map((group) => (
                    <section key={group.label} className="space-y-2.5 pt-1">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                            {group.label}
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            {group.items.map((componentName) => {
                                const previewSrc = getPreviewSrc(componentName);
                                const displayName =
                                    formatComponentName(componentName);
                                return (
                                    <button
                                        key={componentName}
                                        type="button"
                                        onClick={() =>
                                            handleAddComponent(componentName)
                                        }
                                        className="group w-full overflow-hidden rounded-xl border border-neutral-200 bg-white text-left transition hover:border-blue-300 hover:shadow-sm"
                                    >
                                        <div className="bg-gradient-to-r from-primary-700 to-secondary-700 p-2">
                                            <p className="line-clamp-1 text-[11px] font-semibold text-white">
                                                {displayName}
                                            </p>
                                        </div>
                                        <div
                                            className="relative w-full overflow-hidden bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-700"
                                            style={{ aspectRatio: '16 / 9' }}
                                        >
                                            {previewSrc ? (
                                                <img
                                                    src={previewSrc}
                                                    alt={`${displayName} preview`}
                                                    className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                                                    loading="lazy"
                                                    onError={() =>
                                                        handlePreviewError(
                                                            componentName
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <div className="absolute inset-0">
                                                    <div className="absolute inset-x-4 top-12 h-4 rounded-full bg-white/25" />
                                                    <div className="absolute left-4 right-8 top-20 h-3 rounded-full bg-white/20" />
                                                    <div className="absolute left-4 right-16 top-24 h-3 rounded-full bg-white/20" />
                                                    <div className="bg-white/18 absolute bottom-4 left-4 h-14 w-14 rounded-xl" />
                                                    <div
                                                        className="bg-white/14 absolute bottom-4 h-14 w-14 rounded-xl"
                                                        style={{
                                                            left: '5.5rem',
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                ))}

                {groupedComponentOptions.length === 0 && (
                    <p className="text-sm text-neutral-500">
                        No components match your search.
                    </p>
                )}
            </div>
        </EditorDrawer>
    );
}
